import { useEffect, useRef, useState } from 'react'
import type { Device, Transport } from 'mediasoup-client/lib/types'
import { config } from '@/config/env'
import type { SkipNetworkErrorInit } from '@/clientWeb/components/ui/network-error-dialog'
import { buildIceServers } from '../services/turnCredentials.api'
import { SfuSocket } from './sfu-socket'
import { createWsControl, type SfuControl } from './sfu-control'
import { createIceWatchdog, type WatchdogTransport } from '../services/ice-watchdog'
import { sfuJoinRetryDelayMs, sfuSlowRetryDelayMs } from '../services/sfu-join-backoff'
import { sfuNodeUrlFromJoin } from '../services/sfu-node-url'
import { SFU_TAKEN_OVER_ERROR } from '../services/reconnect-banner'
import { vclog } from './vc-log'
import { startClientFlowReport } from '../services/client-flow-report'

/**
 * How many times a session will rebuild itself after a transport has died
 * beyond recovery, before it slows down.
 *
 * Bounded because a rebuild that keeps failing is worse than a visible error:
 * it re-runs the whole join — orchestrator allocation, transports, produce —
 * against a server or network that is not going to cooperate, and hides the
 * failure from the user while it does. Three is enough to ride out a node
 * restart or a laptop changing networks; a fourth means something else is wrong.
 *
 * Reaching it shows the error but is no longer the end: one slow retry follows
 * (SFU_REBUILD_SLOW_RETRY_MS, jittered), and that attempt starts a fresh run of
 * three. A cap that ended the session for good is how a participant stayed in
 * the roster with no media until they reloaded the page.
 */
const MAX_REJOINS = 3

/**
 * The most a join will wait for the previous session's leave to be answered.
 *
 * Every teardown — including the one a rebuild does — sends the orchestrator
 * a leave for this peerId, and the rebuild's join for the SAME peerId follows
 * at once. Both used to be in flight together: if the leave reached the
 * orchestrator after the join, it removed the peer from its SFU and killed the
 * brand-new session (seen 2026-10-06, when thousands of sessions rebuilt at
 * once against a slow orchestrator). The join now waits for that answer — the
 * orchestrator replies only after the SFU has dropped the peer — but no longer
 * than this: a leave that hangs must not stop anyone rejoining.
 */
const LEAVE_WAIT_MS = 3000

/**
 * A rebuilt session that stays up this long counts as recovered, and the
 * rejoin count starts over. Without it MAX_REJOINS was a lifetime cap: three
 * successful recoveries in a long meeting (a Wi-Fi switch, an SFU deploy, a VPN
 * blip) and the fourth network drop showed the error and stayed dead until a
 * reload. The cap is for failures in a row, which this keeps.
 */
const REJOIN_RESET_AFTER_MS = 2 * 60 * 1000

const ORCHESTRATOR_URL = config.sfu.orchestratorUrl

const SFU_API_KEY = config.sfu.apiKey

// The orchestrator authenticates every call with this key alone. An empty value
// is not rejected locally — it is sent as an empty header and comes back as a
// bare 401 from the join call, which reads like a session or permission problem
// rather than a missing build variable. Say so once, at load.
if (!SFU_API_KEY) {
  console.error(
    '[sfu] VITE_SFU_API_KEY is empty — every orchestrator call will return 401. ' +
      'It is intentionally blank in .env.production and must be injected at build ' +
      "time with the value of the orchestrator's API_KEYS.",
  )
}

// Orchestrator join/leave stays REST regardless of the control-plane mode — it
// assigns the node and (un)registers the peer. Only the node-level control plane
// (transports/produce/consume/pause) is swappable via the adapter.
/**
 * How long an orchestrator call may take before it counts as failed.
 *
 * It had no limit: a join sent on a connection a network change left half-dead
 * never settled, so the join's catch — and with it the retry backoff — never
 * ran, and the meeting sat at "connecting" until a reload. Generous, because a
 * loaded orchestrator is slow rather than dead, and a timed-out join is retried.
 */
const ORCH_FETCH_TIMEOUT_MS = 15_000

// Exported for the Screen Share page, which joins the same orchestrator as a
// send-only peer.
export async function orchFetch(baseUrl: string, path: string, method = 'POST', body?: unknown) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ORCH_FETCH_TIMEOUT_MS)
  try {
    return await orchFetchOnce(baseUrl, path, method, body, ctrl.signal)
  } catch (err) {
    if (ctrl.signal.aborted) throw new Error(`SFU orchestrator did not answer within ${ORCH_FETCH_TIMEOUT_MS} ms: ${path}`)
    throw err
  } finally {
    clearTimeout(timer)
  }
}

async function orchFetchOnce(baseUrl: string, path: string, method: string, body: unknown, signal: AbortSignal) {
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'x-api-key': SFU_API_KEY },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    // A failed join is retried, and the meeting says it is reconnecting. The
    // app-wide "Connection Lost" overlay on top of that blocked the meeting —
    // and stayed up after a Wi-Fi switch, which fires no `online` to close it.
    skipNetworkError: true,
  } as SkipNetworkErrorInit)
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string }
    // A 401 here is almost always the build variable, not the user's session —
    // the orchestrator checks x-api-key and nothing else. Name the cause rather
    // than surfacing "Unauthorized", which sends people to look at auth.
    if (res.status === 401) {
      throw new Error(
        SFU_API_KEY
          ? `SFU orchestrator rejected the API key (401). VITE_SFU_API_KEY does not match the orchestrator's API_KEYS.`
          : `SFU orchestrator returned 401: VITE_SFU_API_KEY was empty at build time.`,
      )
    }
    throw new Error(err.error || `HTTP ${res.status} ${path}`)
  }
  return res.json()
}

/**
 * The SFU already has this transport's DTLS parameters.
 *
 * A first connectTransport whose reply was lost — its control socket replaced
 * while the request was out — has usually been applied all the same: the SFU
 * runs a request whatever becomes of the socket it came on. mediasoup refuses
 * a second connect on one transport ("connect() already called"), and that
 * refusal used to fail the transport for good: mediasoup-client asks again on
 * every later produce or consume, and every ask was refused. The parameters
 * cannot differ between the two asks — they come from this transport's own
 * certificate — so the refusal means the connect is done.
 */
export function isAlreadyConnected(err: unknown): boolean {
  return /connect\(\) already called/i.test((err as Error)?.message ?? '')
}

/**
 * The SFU's answer when the node no longer has this peer ("Peer <id> not
 * found in room <id>", Room._requirePeer and the readyToConsume handler) or
 * the whole room ("Room not found", requireRoom in ws/handlers.js).
 */
export function isSessionGoneError(err: unknown): boolean {
  const msg = (err as Error)?.message ?? ''
  return /\bpeer\b.*\bnot found\b/i.test(msg) || /\broom not found\b/i.test(msg)
}

export type SfuJoinState = {
  sfuNodeUrl: string | null
  device: Device | null
  sendTransport: Transport | null
  recvTransport: Transport | null
  /** Control-plane adapter (REST or WS). Consumers/local-media use this for all
   *  node-level ops + WS push subscriptions. Null until the join completes. */
  control: SfuControl | null
  peerId: string
  roomId: string
  ready: boolean
  error: string | null
}

export function useSfuJoin(
  roomId: string | null,
  peerId: string | null,
  sendEventRef: React.MutableRefObject<((event: string, payload: Record<string, unknown>) => void) | null>,
): SfuJoinState {
  const [state, setState] = useState<SfuJoinState>({
    sfuNodeUrl: null,
    device: null,
    sendTransport: null,
    recvTransport: null,
    control: null,
    peerId: peerId ?? '',
    roomId: roomId ?? '',
    ready: false,
    error: null,
  })

  const cleanupRef = useRef<(() => void) | null>(null)
  const mountedRef = useRef(true)
  /**
   * Bumping this re-runs the join effect, which tears everything down and
   * builds it again. It is the only way back from a transport the SFU has
   * closed: `restartIce` cannot revive a transport the server has forgotten,
   * and nothing else in the hook re-creates one.
   */
  const [rejoinToken, setRejoinToken] = useState(0)
  const rejoinCountRef = useRef(0)
  /**
   * Joins that have failed in a row — the orchestrator refusing or failing the
   * allocation, the node refusing the registration, anything before the session
   * was up. Drives the retry backoff; a join that succeeds puts it back to 0.
   * Kept apart from rejoinCountRef on purpose: a join that never came up is not
   * a session that died, and must not spend the rebuild budget.
   */
  const joinFailuresRef = useRef(0)
  /**
   * Leaves still on their way, by room and peer. A join waits for its own
   * room and peer's (bounded by LEAVE_WAIT_MS) so a leave cannot land after
   * the join and take the new session down with it. Another room's leave is no
   * reason for this one to wait: it touches only that room's peer.
   */
  const pendingLeavesRef = useRef<Map<string, Promise<unknown>>>(new Map())
  /**
   * Leaves that never reached the orchestrator — sent while the network was
   * down — by room and peer. The SFU still holds such a peer (it reaps one
   * whose media stayed dead only after ~75 s), and its join is idempotent: a
   * rejoin inside that window inherited the old peer, and `readyToConsume`
   * replayed the old consumers, which live on the dead transport. Every remote
   * tile stayed an avatar until a reload (measured 2026-10-13, 30 s Wi-Fi drop
   * with 20 peers). So the next join delivers them first.
   *
   * Keyed, not a flag: the next join can be for another room (a breakout
   * move during the outage), and a leave sent for that room does not remove
   * the peer left behind in this one.
   */
  const owedLeavesRef = useRef<Map<string, { roomId: string; peerId: string }>>(new Map())
  /**
   * /joins sent so far, by room and peer: the generation a leave belongs to.
   * Once a /join has placed the peer again, an older leave's outcome says
   * nothing about the peer that is there now — its "delivered" must not pay
   * the leave that peer is owed, its "lost" must not owe one for it, and a
   * resend from before must not be reused as that peer's leave.
   */
  const joinGenRef = useRef<Map<string, number>>(new Map())
  /**
   * Resends of owed leaves still on their way, by room and peer, each with
   * the generation it was sent in: one at a time for each. Only resends — a
   * teardown's leave is always sent afresh.
   */
  const resendsInFlightRef = useRef<Map<string, { gen: number; promise: Promise<boolean> }>>(new Map())
  /**
   * Joins in a row held back by an owed resend still unanswered after
   * LEAVE_WAIT_MS, by room and peer. See the slow case in join().
   */
  const slowResendsRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!roomId || !peerId) return

    let cancelled = false
    let sendT: Transport | null = null
    let recvT: Transport | null = null
    let control: SfuControl | null = null
    // Off unless a traced SFU asks for it in its hello (see below).
    let stopFlowReport: (() => void) | null = null
    // A retry this session has scheduled — after a failed join, or the slow one
    // after the rebuild cap. Cleared on teardown, so an unmounted page or a
    // changed room never gets a token bump (and a setState) from a dead effect.
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    // Set by those retries just before they bump the token: the teardown they
    // cause then keeps the error on screen instead of clearing it, so the UI
    // (and an embedding app's MEDIA_CONNECTION_FAILED) does not flicker back to
    // "connecting" on every attempt. The next session that comes up clears it.
    let keepErrorOnTeardown = false
    // What the pending retry runs first (the slow retry's reset), so the
    // `online` fast path below can run it early, exactly as the timer would.
    let retryBefore: (() => void) | undefined
    // This session's ICE watchdogs once it is up — kicked on `online` and on a
    // control-socket reconnect.
    let activeWatchdogs: Array<{ kick: (reason: string) => void }> = []
    // Another tab or window of this person's took this peer over (the SFU
    // closed our control socket 4000 'replaced'). From then on this session
    // does nothing: no rebuild, no retry — either would take the peer back,
    // and the two tabs would trade it for ever — and no leave on teardown,
    // which would remove the peer the other tab is now using.
    let takenOver = false

    const runRetry = () => {
      retryTimer = null
      if (cancelled || !mountedRef.current || takenOver) return
      const before = retryBefore
      retryBefore = undefined
      before?.()
      keepErrorOnTeardown = true
      setRejoinToken((n) => n + 1)
    }

    const scheduleRetry = (delayMs: number, beforeRetry?: () => void) => {
      if (retryTimer !== null) clearTimeout(retryTimer)
      retryBefore = beforeRetry
      retryTimer = setTimeout(runRetry, delayMs)
    }

    // The network is back. Nothing here used to listen: a join that failed
    // during the outage waited out a backoff that had grown the whole time
    // (up to 30 s, or 60 s after the rebuild cap), and a session that survived
    // waited for the watchdog's next 5–20 s tick to notice a dead path.
    //   - a retry waiting: run it now, from a fresh backoff;
    //   - a session up: check both transports now. A path the network change
    //     killed gets its ICE restart at once, and a session the SFU dropped
    //     meanwhile answers that restart "not found" and is rebuilt at once.
    const onOnline = () => {
      if (cancelled || !mountedRef.current || takenOver) return
      if (retryTimer !== null) {
        clearTimeout(retryTimer)
        joinFailuresRef.current = 0
        vclog('join', 'network back — retrying the join now', { roomId, peerId })
        runRetry()
        return
      }
      activeWatchdogs.forEach((w) => w.kick('browser online'))
    }
    const hasWindow = typeof window !== 'undefined'
    if (hasWindow) window.addEventListener('online', onOnline)

    const keyFor = (t: { roomId: string; peerId: string }) => `${t.roomId}|${t.peerId}`
    const myKey = keyFor({ roomId, peerId })
    const genOf = (key: string) => joinGenRef.current.get(key) ?? 0

    /** Keep a leave for its room and peer's next join to wait on. */
    const trackPending = (key: string, leave: Promise<unknown>) => {
      const earlier = pendingLeavesRef.current.get(key)
      pendingLeavesRef.current.set(key, earlier ? Promise.all([earlier, leave]) : leave)
    }

    /**
     * POST the orchestrator leave. True once the orchestrator has answered —
     * with any status: a peer it has already forgotten is just as gone, and
     * the body is not read, so a connection lost after the answer (the body
     * read fails) does not make a delivered leave look lost. False when the
     * request never got there, and the leave stays owed — unless a /join has
     * placed the peer again since, which makes this outcome about a peer that
     * is no longer there (see joinGenRef).
     */
    const sendLeave = (target: { roomId: string; peerId: string } = { roomId, peerId }): Promise<boolean> => {
      const key = keyFor(target)
      const gen = genOf(key)
      return fetch(`${ORCHESTRATOR_URL}/api/v2/rooms/${target.roomId}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': SFU_API_KEY },
        body: JSON.stringify({ peerId: target.peerId }),
        // A lost leave is handled — owed, and resent before the next join —
        // so it must not black out the app with "Connection Lost".
        skipNetworkError: true,
      } as SkipNetworkErrorInit).then(() => true, () => false)
        .then((delivered) => {
          if (genOf(key) === gen) {
            if (delivered) {
              owedLeavesRef.current.delete(key)
            } else {
              owedLeavesRef.current.set(key, target)
              vclog('join', 'leave never reached the orchestrator — owed until the next join', target)
            }
          }
          return delivered
        })
    }

    /** Resend an owed leave, or hand back this generation's resend of it already under way. */
    const resendLeave = (target: { roomId: string; peerId: string }): Promise<boolean> => {
      const key = keyFor(target)
      const gen = genOf(key)
      const inFlight = resendsInFlightRef.current.get(key)
      if (inFlight && inFlight.gen === gen) return inFlight.promise
      const promise: Promise<boolean> = sendLeave(target).finally(() => {
        if (resendsInFlightRef.current.get(key)?.promise === promise) resendsInFlightRef.current.delete(key)
      })
      resendsInFlightRef.current.set(key, { gen, promise })
      return promise
    }

    async function join() {
      try {
        // 0. Let the previous session's leave land first. On a rebuild it is
        // for this same peerId, so a leave the orchestrator processes AFTER
        // this join removes the peer from the SFU and kills the session being
        // built. Bounded: a leave that hangs costs LEAVE_WAIT_MS, not the rejoin.
        const pendingLeave = pendingLeavesRef.current.get(myKey)
        if (pendingLeave) {
          const startedAt = Date.now()
          let waitTimer: ReturnType<typeof setTimeout> | undefined
          const answered = await Promise.race([
            pendingLeave.then(() => true),
            new Promise<boolean>((resolve) => { waitTimer = setTimeout(() => resolve(false), LEAVE_WAIT_MS) }),
          ])
          clearTimeout(waitTimer)
          if (pendingLeavesRef.current.get(myKey) === pendingLeave) pendingLeavesRef.current.delete(myKey)
          vclog('join', 'waited for previous leave', {
            roomId, peerId, answered, waitedMs: Date.now() - startedAt,
          })
          if (cancelled || !mountedRef.current) return
        }

        // 0b. Leaves that never got out. All of them go now; one for this
        // very room and peer must land before the join, or the join lands on
        // that stale peer — its consumers live on a dead transport, and every
        // tile stays an avatar with nothing to notice it.
        const owed = [...owedLeavesRef.current.values()]
        const owedHere = owed.find((o) => keyFor(o) === myKey)
        for (const o of owed) {
          // Another room's peer: not waited for by this join, but kept for
          // the next join back into that room, which must not be overtaken.
          if (o !== owedHere) trackPending(keyFor(o), resendLeave(o))
        }
        // Another room's leave still out — the teardown of a breakout move
        // made a moment ago — is not known lost yet. Resend it once it is,
        // still without holding this join back for it.
        for (const [key, leave] of pendingLeavesRef.current) {
          if (key === myKey) continue
          void leave.then(() => {
            const o = owedLeavesRef.current.get(key)
            if (o && !cancelled && mountedRef.current) trackPending(key, resendLeave(o))
          })
        }
        // The resend this join went ahead of without its answer (see below).
        let wentAheadOf: Promise<boolean> | null = null
        if (owedHere) {
          const resend = resendLeave(owedHere)
          trackPending(myKey, resend)
          let waitTimer: ReturnType<typeof setTimeout> | undefined
          const outcome = await Promise.race([
            resend.then((delivered) => (delivered ? 'delivered' as const : 'lost' as const)),
            new Promise<'slow'>((resolve) => { waitTimer = setTimeout(() => resolve('slow'), LEAVE_WAIT_MS) }),
          ])
          clearTimeout(waitTimer)
          vclog('join', 'resent the leave the network swallowed', { roomId, peerId, outcome })
          if (cancelled || !mountedRef.current) return
          // Refused by the network: it is still down, and the join would fail
          // anyway. Fail it here, and the join-failure backoff brings us back.
          if (outcome === 'lost') throw new Error('previous leave not delivered — network still down')
          if (outcome === 'slow') {
            // Unanswered is not "sent": fetch cannot tell a request the
            // orchestrator is still working on from one still waiting for a
            // connection, and a join sent behind the second lands on the stale
            // peer. So once, hold the join back as if refused — the retry
            // waits for this resend again. A second time in a row, the
            // orchestrator is slow rather than the network down, and holding
            // media back for it any longer costs more than the race.
            const held = (slowResendsRef.current.get(myKey) ?? 0) + 1
            slowResendsRef.current.set(myKey, held)
            if (held < 2) throw new Error('previous leave not answered yet')
            wentAheadOf = resend
          }
          slowResendsRef.current.delete(myKey)
        }

        // A /join placed or re-placed this peer: leaves sent before it are
        // about the peer that was there before (see joinGenRef).
        joinGenRef.current.set(myKey, genOf(myKey) + 1)

        // 1. Orchestrator join — get RTP capabilities + assigned node
        const joinData = await orchFetch(
          ORCHESTRATOR_URL,
          `/api/v2/rooms/${roomId}/join`,
          'POST',
          { peerId },
        ) as {
          rtpCapabilities: unknown
          existingProducers: unknown[]
          nodeUrl: string
          gatewayNodeUrl?: string
          nodeId: string
        }

        if (cancelled) return

        // Resolve the node URL the same way video.html does
        const sfuNodeUrl = sfuNodeUrlFromJoin(joinData, ORCHESTRATOR_URL)

        // 1b. Build the WS control plane — the single path between client and
        // node. No REST/polling fallback (the shared REST routes remain on the
        // node for mobile/portal/H.323, but the web client no longer uses them).
        const socket = new SfuSocket({ nodeUrl: sfuNodeUrl, roomId: roomId!, peerId: peerId!, key: SFU_API_KEY })
        control = createWsControl(socket)
        // A node running MEDIA_FLOW_LOG asks, in the hello it sends on every
        // (re)connect, for this browser's own receive/send summary every
        // `clientStatsMs`. Without that field nothing is collected or sent.
        const flowControl = control
        control.on('hello', (data: { clientStatsMs?: number } | undefined) => {
          stopFlowReport?.()
          stopFlowReport = null
          const ms = data?.clientStatsMs
          if (typeof ms === 'number' && ms > 0 && flowControl.clientStats) {
            stopFlowReport = startClientFlowReport((report) => flowControl.clientStats!(report), ms)
            vclog('socket', 'client flow report on', { intervalMs: ms })
          }
        })
        // Subscribed before anything else can fail on the socket: a takeover
        // mid-join rejects the join's requests, and that failure must not be
        // retried either.
        control.onReplaced(() => {
          if (cancelled || !mountedRef.current) return
          takenOver = true
          vclog('join', 'media session taken over by another tab or window — stopping here', { roomId, peerId })
          if (retryTimer !== null) clearTimeout(retryTimer)
          retryTimer = null
          cleanupRef.current?.()
          cleanupRef.current = null
          setState((prev) => ({
            ...prev,
            sendTransport: null,
            recvTransport: null,
            control: null,
            ready: false,
            error: SFU_TAKEN_OVER_ERROR,
          }))
        })
        await control.join() // idempotent addPeer on the assigned node
        vclog('join', 'ws control ready', { roomId, peerId, sfuNodeUrl })
        if (cancelled) { control.close(); return }

        // 2. Load mediasoup Device
        const { Device: DeviceClass } = await import('mediasoup-client')
        const device = new DeviceClass()
        await device.load({ routerRtpCapabilities: joinData.rtpCapabilities as never })

        if (cancelled) { control.close(); return }

        // 3. Create send transport
        const sendData = await control.createTransport('send') as {
          transportId: string
          iceParameters: unknown
          iceCandidates: unknown[]
          dtlsParameters: unknown
        }

        if (cancelled) { control.close(); return }

        // Fetched here rather than at module load: the TURN username is an
        // expiry timestamp, so a value built at startup is refused by coturn
        // once the TTL passes.
        const iceServers = await buildIceServers()
        vclog('join', 'ice servers resolved', { count: iceServers.length })
        if (cancelled) { control.close(); return }

        sendT = device.createSendTransport({
          id: sendData.transportId,
          iceParameters: sendData.iceParameters as never,
          iceCandidates: sendData.iceCandidates as never,
          dtlsParameters: sendData.dtlsParameters as never,
          iceServers,
        })
        vclog('transport', 'send transport created', { transportId: sendT.id })

        // Set once rebuildSession exists, below. A transport's first connect
        // happens on its first produce or consume, after the session is up.
        let requestRebuild: ((reason: string) => void) | null = null
        sendT.on('connect', async ({ dtlsParameters }, callback, errback) => {
          try {
            await control!.connectTransport(sendT!.id, dtlsParameters)
            vclog('transport', 'send transport connected (dtls)', { transportId: sendT!.id })
            callback()
          } catch (e) {
            if (isAlreadyConnected(e)) {
              vclog('transport', 'send transport was already connected (an earlier reply was lost)', { transportId: sendT!.id })
              callback()
              return
            }
            errback(e as Error)
            // A first connect that failed wedges this transport for good:
            // mediasoup-client added the transceiver before asking and never
            // rolls it back, so every later produce or consume on it fails, and
            // the ICE watchdog skips a transport still 'new'. Only a new
            // session recovers — reproduced 3/3 in review, after a reconnect
            // rejected the in-flight connect with "socket closed".
            vclog('transport', 'send transport connect failed — rebuilding session', { transportId: sendT!.id, error: (e as Error).message })
            requestRebuild?.(`send transport connect failed: ${(e as Error).message}`)
          }
        })

        sendT.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
          try {
            const data = await control!.produce(sendT!.id, kind, rtpParameters, appData)
            const sourceType = (appData as Record<string, string>)?.sourceType || 'camera'
            vclog('producer', 'produced', { producerId: data.producerId, kind, sourceType })
            // Keep broadcasting via Phoenix so cross-node peers learn about the
            // producer even before the mesh→WS push exists. Same-node WS peers
            // also get the server's `newProducer` push; consumers dedupe.
            sendEventRef.current?.('media:new_producer', { producerId: data.producerId, kind, sourceType })
            callback({ id: data.producerId })
          } catch (e) { errback(e as Error) }
        })

        // State changes are worth recording, but they are NOT the recovery
        // trigger any more — see `services/ice-watchdog.ts`. mediasoup-client
        // suppresses a repeat of the same state, so a transport that settles in
        // `failed` never fires this again, and the single restart attempt that
        // used to hang off it was both the only one and the one most likely to
        // be lost.
        sendT.on('connectionstatechange', (connectionState) => {
          vclog('transport', 'send connectionstate', { transportId: sendT!.id, connectionState })
        })

        // 4. Create recv transport
        const recvData = await control.createTransport('recv') as {
          transportId: string
          iceParameters: unknown
          iceCandidates: unknown[]
          dtlsParameters: unknown
        }

        if (cancelled) { control.close(); return }

        recvT = device.createRecvTransport({
          id: recvData.transportId,
          iceParameters: recvData.iceParameters as never,
          iceCandidates: recvData.iceCandidates as never,
          dtlsParameters: recvData.dtlsParameters as never,
          iceServers,
        })
        vclog('transport', 'recv transport created', { transportId: recvT.id })

        recvT.on('connect', async ({ dtlsParameters }, callback, errback) => {
          try {
            await control!.connectTransport(recvT!.id, dtlsParameters)
            vclog('transport', 'recv transport connected (dtls)', { transportId: recvT!.id })
            callback()
          } catch (e) {
            if (isAlreadyConnected(e)) {
              vclog('transport', 'recv transport was already connected (an earlier reply was lost)', { transportId: recvT!.id })
              callback()
              return
            }
            errback(e as Error)
            // A first connect that failed wedges this transport for good:
            // mediasoup-client added the transceiver before asking and never
            // rolls it back, so every later produce or consume on it fails, and
            // the ICE watchdog skips a transport still 'new'. Only a new
            // session recovers — reproduced 3/3 in review, after a reconnect
            // rejected the in-flight connect with "socket closed".
            vclog('transport', 'recv transport connect failed — rebuilding session', { transportId: recvT!.id, error: (e as Error).message })
            requestRebuild?.(`recv transport connect failed: ${(e as Error).message}`)
          }
        })

        recvT.on('connectionstatechange', (connectionState) => {
          vclog('transport', 'recv connectionstate', { transportId: recvT!.id, connectionState })
        })

        if (cancelled || !mountedRef.current) { control.close(); return }

        // The INITIAL readyToConsume is not sent here any more — useConsumers
        // sends it, from the effect that subscribes to `newConsumer`, once its
        // handlers exist.
        //
        // It used to be sent here, before the setState below had even told React
        // about this control. readyToConsume makes the SFU push `newConsumer` for
        // every producer in the room at once, and useConsumers only subscribes
        // after the re-render that setState triggers. Every push landing in that
        // gap hit "[sfu-socket] no handler for server notification newConsumer"
        // and was gone for good. The gap is widest exactly when it hurts: a
        // session rebuilt after an SFU restart (a whole room of pushes at once),
        // in a background tab (React's render is throttled, the socket is not).
        // Found 2026-09-28: a browser came back from an SFU restart with 66
        // consumers pushed, 0 registered, and no remote video at all.
        const rtpCaps = device.rtpCapabilities

        // Reconnect re-sync: the control socket blipping deregisters us from the
        // node's push registry, so any `newConsumer`/`producerPaused` pushed while
        // we were down is lost. On every reconnect, re-assert readiness — the SFU
        // re-creates any missing consumers and re-pushes ALL of them (client
        // dedupes), recovering the dropped notifications with no polling.
        control.onReconnect(() => {
          vclog('consumer', 'readyToConsume (reconnect re-sync)', { peerId })
          control!.readyToConsume(rtpCaps).catch((err) => {
            // The node no longer has this peer (or room) — it was removed
            // while we were away. Nothing on this session can work, so build
            // a new one now rather than when the watchdog finds out. Needs the
            // SFU that answers this with an error (handlers.js, 2026-10-13);
            // an older one answers ok and the watchdog kick below covers it.
            if (isSessionGoneError(err)) {
              vclog('join', 'SFU no longer has this peer — rebuilding', { roomId, peerId, error: (err as Error).message })
              rebuildSession('peer gone from the SFU', false)
            }
          })
          // The socket dropping usually means the network did, so check the
          // transports now rather than at the watchdog's next tick. This is
          // also how a session the SFU has dropped is found quickly:
          // readyToConsume answers ok for a peer the node no longer has
          // (handlers.js), but restartIce on its transport answers "not
          // found", which the watchdog turns into a rebuild.
          activeWatchdogs.forEach((w) => w.kick('control socket reconnected'))
        })

        // One rebuild per session, whoever asks first: the SFU's `sessionLost`
        // push and the ICE watchdog both notice a dead worker, seconds apart.
        let rebuilding = false
        const rebuildSession = (reason: string, countsTowardCap: boolean) => {
          if (rebuilding || cancelled || !mountedRef.current || takenOver) return
          if (countsTowardCap) {
            if (rejoinCountRef.current >= MAX_REJOINS) {
              // Both transports' watchdogs give up on the same dead session;
              // the first one to get here has already said so and scheduled
              // the slow retry.
              if (retryTimer !== null) return
              // Stop pretending. A user staring at a working-looking call
              // whose media reaches nobody is the failure mode this whole
              // change exists to end. The error stays up while we wait.
              setState((prev) => ({
                ...prev,
                ready: false,
                error: `media connection lost (${reason}) and could not be rebuilt`,
              }))
              // ...but do not give up for good. Stopping here is how a
              // participant sat in the roster with no media until a reload.
              // One slow retry, and it gets a fresh run of quick rebuilds.
              // A `sessionLost` push meanwhile still rebuilds at once (it
              // does not count toward the cap), and its teardown clears this.
              const delay = sfuSlowRetryDelayMs()
              vclog('join', `rebuild cap reached — slow retry in ${(delay / 1000).toFixed(1)}s`, {
                roomId, peerId, reason, rejoins: rejoinCountRef.current,
              })
              scheduleRetry(delay, () => {
                rejoinCountRef.current = 0
                rebuilding = true
              })
              return
            }
            rejoinCountRef.current += 1
          }
          rebuilding = true
          setRejoinToken((n) => n + 1)
        }

        // The SFU says this session is gone — the mediasoup worker carrying it
        // crashed. The node itself is fine and so is this control socket, so
        // nothing else would tell us until the ICE watchdog noticed the dead
        // transports seconds later. Not counted against MAX_REJOINS: that cap
        // is for a network that keeps failing, and a server-side crash loop is
        // bounded on the server (it restarts the node after 3 in 10 minutes).
        // The owed resend this join went ahead of, after two slow answers in a
        // row: if it turns out never to have arrived, the join landed on the
        // stale peer — its consumers on a dead transport, every tile an
        // avatar, and nothing else that would notice. Rebuild once: the
        // teardown sends a fresh leave, and the next join waits for it.
        if (wentAheadOf) {
          void wentAheadOf.then((delivered) => {
            if (delivered || cancelled || !mountedRef.current) return
            vclog('join', 'the leave this session went ahead of was lost — rebuilding', { roomId, peerId })
            rebuildSession('previous leave lost after the join', false)
          })
        }

        requestRebuild = (reason) => rebuildSession(reason, true)

        const offSessionLost = control.on('sessionLost', (data: { reason?: string }) => {
          vclog('transport', 'SFU reported this session lost — rebuilding', { reason: data?.reason })
          rebuildSession(`server: ${data?.reason ?? 'session lost'}`, false)
        })

        // Level-triggered ICE recovery for both directions.
        //
        // A send transport that loses its path is the worse of the two and the
        // harder to notice: nothing on screen changes, because the failure is
        // entirely in what OTHER people can see. Measured in production, a send
        // transport sat in `failed` with no candidate pair for 37 minutes while
        // the encoder kept producing — and not one line was logged anywhere,
        // because the only recovery path swallowed its own failure.
        const watchdogs = (['send', 'recv'] as const).map((which) => {
          const t = (which === 'send' ? sendT : recvT) as unknown as WatchdogTransport
          return createIceWatchdog({
            transport: t,
            requestIceParameters: (id) => control!.restartIce(id) as Promise<{ iceParameters: unknown }>,
            onLog: (event, detail) => vclog('transport', `${which}: ${event}`, detail),
            onUnrecoverable: (reason) => {
              vclog('transport', `${which} transport unrecoverable — rebuilding session`, {
                transportId: t.id, reason, rejoins: rejoinCountRef.current,
              })
              rebuildSession(reason, true)
            },
          })
        })

        activeWatchdogs = watchdogs

        // Healthy for long enough — the next failure is a new run, not a 4th try.
        const rejoinReset = setTimeout(() => { rejoinCountRef.current = 0 }, REJOIN_RESET_AFTER_MS)

        cleanupRef.current = () => {
          clearTimeout(rejoinReset)
          stopFlowReport?.()
          stopFlowReport = null
          offSessionLost()
          activeWatchdogs = []
          watchdogs.forEach((stop) => stop())
          sendT?.close()
          recvT?.close()
          control?.close()
        }

        // Up: the next failed join is the first of a new run.
        joinFailuresRef.current = 0
        setState({
          sfuNodeUrl,
          device,
          sendTransport: sendT,
          recvTransport: recvT,
          control,
          peerId: peerId!,
          roomId: roomId!,
          ready: true,
          error: null,
        })
      } catch (err) {
        // A join that got as far as a transport leaves a live RTCPeerConnection
        // behind if only the socket is closed. Harmless when a failed join was
        // final; with retries it would be one more leaked connection per try.
        stopFlowReport?.()
        stopFlowReport = null
        sendT?.close()
        recvT?.close()
        control?.close()
        if (cancelled || !mountedRef.current || takenOver) return
        // A failed join used to be final: the error went up and nothing ever
        // tried again, so a peer the orchestrator turned away during an
        // overload (2026-10-06: 248 joins answered "No SFU node took the
        // peer") stayed in the meeting with no media until a reload. Show the
        // error, and try again on a jittered backoff so a fleet refused
        // together does not return together. Not counted against MAX_REJOINS.
        joinFailuresRef.current += 1
        const delay = sfuJoinRetryDelayMs(joinFailuresRef.current)
        const message = (err as Error).message
        vclog('join', `join failed — retrying in ${(delay / 1000).toFixed(1)}s`, {
          roomId, peerId, failures: joinFailuresRef.current, error: message,
        })
        setState((prev) => ({ ...prev, error: message, ready: false }))
        scheduleRetry(delay)
      }
    }

    void join()

    return () => {
      cancelled = true
      if (hasWindow) window.removeEventListener('online', onOnline)
      if (retryTimer !== null) clearTimeout(retryTimer)
      retryTimer = null
      cleanupRef.current?.()
      cleanupRef.current = null
      // Tell SFU the peer left. Not awaited here — a teardown cannot wait —
      // but kept, so the next join for this peer waits for it to land rather
      // than racing it (see LEAVE_WAIT_MS). Chained onto one still in flight
      // so a quick succession of teardowns is waited for as a whole.
      // Not once taken over: the peer is the other tab's now.
      if (!takenOver) trackPending(myKey, sendLeave())
      const keepError = keepErrorOnTeardown || takenOver
      setState((prev) => ({
        sfuNodeUrl: null,
        device: null,
        sendTransport: null,
        recvTransport: null,
        control: null,
        peerId: peerId ?? '',
        roomId: roomId ?? '',
        ready: false,
        error: keepError ? prev.error : null,
      }))
    }
  // `rejoinToken` belongs here: bumping it is how an unrecoverable transport
  // gets a whole new session, since nothing else in this hook rebuilds one.
  }, [roomId, peerId, rejoinToken]) // eslint-disable-line react-hooks/exhaustive-deps

  return state
}
